import api from './client';
import type { AxiosResponse, AxiosProgressEvent } from 'axios';

interface DiscoverFilters {
  age_min?: number;
  age_max?: number;
  budget?: string;
  intention?: string;
  verified_only?: boolean;
  has_badge?: boolean;
}

export const updateProfile = (data: Record<string, unknown>): Promise<AxiosResponse> => api.put('/profiles/me', data);
export const discoverProfiles = (): Promise<AxiosResponse> => api.get('/profiles/discover');
export const createSwipe = (data: { swiped_id: string; direction: string }): Promise<AxiosResponse> => api.post('/swipes', data);
export const getMatches = (): Promise<AxiosResponse> => api.get('/matches');

// Public preview of the signed-in user's profile — exactly the fields other
// daters see on the swipe card. Used by the post-wizard "Preview my profile"
// modal so users can confirm what landed on their card before publishing.
export type ProfilePreview = {
  id: string;
  name: string;
  age?: number;
  bio?: string;
  location?: string;
  photos?: string[];
  job_title?: string;
  company?: string;
  school?: string;
  education?: string;
  height?: string;
  interests?: string[];
  budget_preference?: string;
  intention?: string;
  core_values?: string[];
  deal_breakers?: string[];
  languages?: string[];
  smoking?: string;
  drinking?: string;
  exercise?: string;
  prompts?: Array<{ question: string; answer: string }>;
  verified?: boolean;
  fair_partner_badge?: boolean;
  fair_partner_score?: number;
  is_premium?: boolean;
  onboarding_complete?: boolean;
  missing_fields: string[];
};
export const getProfilePreview = (): Promise<AxiosResponse<ProfilePreview>> =>
  api.get('/profiles/preview');

// "Polish my bio again" — passes the current bio through the BIO_COACH_PROMPT
// for another pass. Returns the new bio without saving (caller decides).
export const rewriteBio = (bio: string): Promise<AxiosResponse<{ bio: string }>> =>
  api.post('/ai/rewrite-bio', { bio });

export const discoverWithFilters = (filters: DiscoverFilters = {}): Promise<AxiosResponse> => {
  const params = new URLSearchParams();
  if (filters.age_min) params.append('age_min', String(filters.age_min));
  if (filters.age_max) params.append('age_max', String(filters.age_max));
  if (filters.budget) params.append('budget', filters.budget);
  if (filters.intention) params.append('intention', filters.intention);
  if (filters.verified_only) params.append('verified_only', 'true');
  if (filters.has_badge) params.append('has_badge', 'true');
  return api.get(`/profiles/discover?${params.toString()}`);
};

// Swipe extras
export const getSwipesRemaining = (): Promise<AxiosResponse> => api.get('/swipes/remaining');
export const getReceivedLikes = (): Promise<AxiosResponse> => api.get('/likes/received');
export const rewindLastSwipe = (): Promise<AxiosResponse> => api.post('/swipes/rewind');
export const trackProfileView = (profileId: string, durationMs: number): Promise<AxiosResponse> => api.post(`/swipes/track-view?profile_id=${profileId}&duration_ms=${durationMs}`);
export const superLike = (userId: string): Promise<AxiosResponse> => api.post(`/swipes/superlike/${userId}`);
export const getSuperlikesRemaining = (): Promise<AxiosResponse> => api.get('/swipes/superlikes-remaining');
export const getTopPick = (): Promise<AxiosResponse> => api.get('/matching/top-pick');

// Match actions
export const toggleFavorite = (matchId: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/favorite`);
export const toggleArchive = (matchId: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/archive`);
export const toggleLike = (matchId: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/like`);
export const markMatchViewed = (matchId: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/mark-viewed`);
export const unmatchUser = (matchId: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/unmatch`);
export const getUnmatchTemplates = (): Promise<AxiosResponse> => api.get('/templates/unmatch');
export const submitWeMet = (matchId: string, didMeet: boolean, quality?: string): Promise<AxiosResponse> => api.post(`/matches/${matchId}/we-met?did_meet=${didMeet}${quality ? `&quality=${quality}` : ''}`);
export const getExpiringMatches = (): Promise<AxiosResponse> => api.get('/matches/expiring');
export const getDateHistory = (matchId: string): Promise<AxiosResponse> => api.get(`/matches/${matchId}/date-history`);

// V3 Matching
export const getV3MatchScore = (candidateId: string): Promise<AxiosResponse> => api.get(`/matching/v3/score?candidate_id=${candidateId}`);
export const getCompatibilityReport = (candidateId: string): Promise<AxiosResponse> => api.get(`/matching/v3/compatibility-report?candidate_id=${candidateId}`);

// AI Matchmaker (replaces swipe browsing)
export const getMatchmakerDashboard = (): Promise<AxiosResponse> => api.get('/matchmaker/dashboard');
export const getMatchmakerReport = (queueId: string): Promise<AxiosResponse> => api.get(`/matchmaker/report/${queueId}`);
export const keepMatch = (queueId: string): Promise<AxiosResponse> => api.post(`/matchmaker/queue/${queueId}/keep`);
export const removeMatch = (queueId: string): Promise<AxiosResponse> => api.post(`/matchmaker/queue/${queueId}/remove`);
export const getMatchmakerInsights = (): Promise<AxiosResponse> => api.get('/matchmaker/insights');

// Profile extras
export const reorderPhotos = (order: string[]): Promise<AxiosResponse> => api.put('/profiles/me/photos/reorder', order);
export const getProfileInsights = (): Promise<AxiosResponse> => api.get('/users/me/insights');
export const getResponseRate = (userId: string): Promise<AxiosResponse> => api.get(`/users/${userId}/response-rate`);
export const uploadPhoto = (file: File, onUploadProgress?: (e: AxiosProgressEvent) => void): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/photos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress });
};
export const deletePhoto = (fileId: string): Promise<AxiosResponse> => api.delete(`/photos/${fileId}`);
export const verifySelfie = (file: File): Promise<AxiosResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/verify/selfie', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Discovery gender filter (persists to the user's account + rebuilds the queue)
export const getDiscoveryPreferences = (): Promise<AxiosResponse> => api.get('/matchmaker/preferences');
export const updateDiscoveryPreferences = (genderPreference: string): Promise<AxiosResponse> =>
  api.put('/matchmaker/preferences', { gender_preference: genderPreference });

// Quiz
export const getQuizQuestions = (): Promise<AxiosResponse> => api.get('/quiz/questions');
export const submitQuizAnswers = (answers: Record<string, unknown>): Promise<AxiosResponse> => api.post('/quiz/answers', { answers });
export const getQuizCompatibility = (userId: string): Promise<AxiosResponse> => api.get(`/quiz/compatibility/${userId}`);
